<template>
  <div>
    <template v-if="selectedItem">
      <validated-form
        name="menuItem"
        :disabled="order.isAddingItem"
        @dirty="dirtyFormHandler"
        @valid-submit="updateOrder"
      >
        <image-scroller
          v-if="images.length > 0"
          :images="images"
        />

        <div class="section section--half-padding sticky-top">
          <div class="layout layout--horizontal half-spacing">
            <div
              v-if="selectedItem.isNewItem"
              class="new-item-banner"
            >
              New!
            </div>

            <div
              v-if="selectedItem.isLowInventory"
              class="low-inventory-banner"
            >
              <div class="low-inventory-content">
                Low Stock
              </div>

              <div
                v-if="isAtLowStockThreshold"
                class="low-inventory-amount"
              >
                {{ remainingInventory }}
              </div>
            </div>
          </div>

          <div class="layout layout--horizontal">
            <div class="grow spacing">
              <h1 class="title title--3 no-spacing bold">
                {{ selectedItem.displayName }}
              </h1>

              <h1
                v-if="calorieDisplay"
                class="title title--4 no-spacing"
              >
                {{ calorieDisplay }}
              </h1>

              <current-day-menu-schedule
                class="no-spacing"
                :schedule="selectedItem.schedule"
              />
            </div>

            <favorite-button
              v-if="showFavoriteButton && !isReadOnly"
              ref="favoriteButton"
              class="icon--bigger"
              :item="selectedItem"
              :modifiers="modifiers"
              :special-instructions="specialInstructions"
              :validate-form="true"
            />
          </div>
        </div>

        <div
          v-if="selectedItem.descriptionText || dietaryAttributes.length > 0"
          class="section section--half-padding static"
        >
          <v-clamp
            v-if="selectedItem.descriptionText"
            tag="p"
            class="pre-line"
            autoresize
            :max-lines="10"
          >
            {{ selectedItem.descriptionText }}

            <template #after="{ clamped, expanded, toggle }">
              <template v-if="clamped || expanded">
                <br>
                <button
                  class="link primary"
                  type="button"
                  @click="toggle"
                >
                  {{ clamped ? 'Read More' : 'Read Less' }}
                </button>
              </template>
            </template>
          </v-clamp>

          <div
            v-if="dietaryAttributes.length > 0"
            class="layout layout--horizontal layout--justify-start layout--wrap"
          >
            <p
              v-for="attribute in dietaryAttributes"
              :key="attribute.name"
              class="layout layout--icon"
            >
              <img
                class="image-icon image-icon--24"
                :src="attribute.imageUrl"
                :alt="attribute.name"
              >
              <span class="smaller bold">{{ attribute.name }}</span>
            </p>
          </div>
        </div>

        <div
          v-if="selectedItem.modifierGroups.length > 0 && !isReadOnly"
          class="section section--half-padding"
        >
          <modifier-groups
            :groups="selectedItem.modifierGroups"
            :read-only="!selectedLocation.storeId || !selectedLocation.isReadyForOrdering || order.groupOrder.isLocked || menu.unavailablePerSchedule"
            :hide-prices="selectedLocation.features.HidePrices"
            :selected-modifiers="selectedModifiers"
            @selected="modifiersSelectedHandler"
          />
        </div>

        <div
          v-if="features.length > 0"
          class="section section--half-padding"
        >
          <h2 class="title title--5 dark-gray regular">Features</h2>

          <ul class="bullet-list light-gray">
            <li
              v-for="feature in features"
              :key="feature.name"
            >
              <p class="dark">{{ feature.name }}</p>
            </li>
          </ul>
        </div>

        <div
          v-if="merchant.options.itemLevelInstructionsEnabled && selectedLocation.storeId && !isReadOnly"
          class="section section--half-padding"
        >
          <text-input
            v-model="specialInstructions"
            class="half-spacing"
            type="text"
            :label="content.ItemLevelInstructions.metadata.inputPlaceholder"
            name="specialInstructions"
            :validations="specialInstructionsValidations"
          />

          <p class="smaller">{{ content.ItemLevelInstructions.metadata.inputSubtitle }}</p>
        </div>

        <div
          v-if="selectedItem.maxAllowed !== 0 && !isReadOnly"
          class="section section--half-padding section--alt-background section--shadow sticky-bottom"
        >
          <group-order-locked-message v-if="order.groupOrder.isLocked" />

          <p
            v-else-if="!selectedLocation.isCurrentServiceAvailable"
            class="smaller"
          >
            Ordering is unavailable at this time.
          </p>

          <div
            v-else-if="!selectedLocation.storeId"
            class="layout layout--horizontal"
          >
            <p class="grow smaller">
              Select a location in order to add items to cart.
            </p>

            <button
              class="button button--secondary button--auto-width"
              type="button"
              @click="$openModal('editOrderDetails')"
            >
              Select Location
            </button>
          </div>

          <p
            v-else-if="!selectedLocation.isReadyForOrdering"
            class="smaller"
          >
            <template v-if="selectedLocation.outsideGeoFence">This store is too far away and can’t accept orders at this time</template>
            <template v-else>This store is currently closed and can’t accept orders at this time.</template>
          </p>

          <template v-else-if="menu.unavailablePerSchedule">
            <p class="smaller">This menu is currently unavailable for ordering. Please check back later.</p>
          </template>

          <template v-else>
            <div class="layout layout--horizontal">
              <quantity-input
                v-model="quantity"
                :max-value="maxAllowed"
                name="menuItemQuantity"
              />

              <button
                :class="['button button--primary button--auto-width', { 'button--loading': order.isAddingItem }]"
                :disabled="!currentQuantityIsValid"
                type="submit"
              >
                <template v-if="selectedOrderItem">
                  Update
                </template>

                <template v-else>
                  Add
                </template>

                <template v-if="selectedLocation.storeId && !selectedLocation.features.HidePrices">
                  {{ $dollars(priceWithModifiers) }}
                </template>
              </button>
            </div>

            <template v-if="!currentQuantityIsValid">
              <hr class="no-spacing-top">

              <p class="smaller center">
                <span
                  v-if="orderValidations.warnings.orderMaximumItemCount"
                  v-html="orderValidations.warnings.orderMaximumItemCount"
                />
                <template v-else>
                  Your cart has reached the limit for this item <b>({{ selectedItem.maxAllowed }})</b>
                </template>
              </p>
            </template>
          </template>
        </div>
      </validated-form>
    </template>


    <div
      v-else
      class="section center"
    >
      <h2 class="title title--3">
        Item Not Available
      </h2>

      <p>We're sorry, this menu item is not available at your selected location.</p>

      <div class="layout layout--horizontal layout--horizontal-equal">
        <button
          type="button"
          class="button button--light button--large"
          @click="$openModal('editOrderDetails')"
        >
          Change
          <span class="show-550-up">Location</span>
        </button>

        <button
          type="button"
          class="button button--primary button--large"
          @click="$closeModal('menuItem')"
        >
          Browse Menu
        </button>
      </div>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';
  import calculatePriceWithModifiers from '@/helpers/calculate-price-with-modifiers';
  import kebabcase from '@/filters/kebabcase';


  export default {
    name: 'MenuItem',

    props: {
      isReadOnly: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        modifiers: [],
        specialInstructions: null,
        quantity: 1
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('menu', ['menu', 'selectedCategory', 'selectedItem', 'selectedFavoriteItem', 'selectedOrderItem']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('orders', ['order', 'orderValidations', 'orderItemQuantitiesPerMenuItem']),

      calorieDisplay() {
        return this.selectedItem.nutritionalInfo?.calories;
      },

      isAtLowStockThreshold() {
        return !!this.selectedItem.inventoryCount;
      },

      remainingInventory() {
        return `${this.selectedItem.inventoryCount} remaining`;
      },

      currentQuantityIsValid() {
        const { orderItemCount, orderItemStagedCount } = this.order;

        const orderMaximumItemCount = this.orderValidations.validations && this.orderValidations.validations.orderMaximumItemCount;

        let isValid = this.selectedItem.maxAllowed === -1 || this.existingQuantity + this.quantity <= this.selectedItem.maxAllowed;

        if (isValid && orderMaximumItemCount) {
          isValid = this.selectedOrderItem
            ? (orderItemCount + orderItemStagedCount) - this.selectedOrderItem.quantity + this.quantity <= orderMaximumItemCount
            : (orderItemCount + orderItemStagedCount) + this.quantity <= orderMaximumItemCount;
        }

        return isValid;
      },

      existingQuantity() {
        let quantity = this.orderItemQuantitiesPerMenuItem[this.selectedItem.menuItemId] || 0;

        if (this.selectedOrderItem) {
          quantity -= this.selectedOrderItem.quantity;
        }

        return quantity;
      },

      dietaryAttributes() {
        return this.selectedItem.attributes
          ?.filter(attribute => ['Allergen', 'FoodType', 'SpiceLevel'].includes(attribute.type))
          .map(attribute => ({ ...attribute, iconName: kebabcase(attribute.name) }));
      },

      features() {
        return this.selectedItem.attributes?.filter(attribute => attribute.type === 'ProductFeature');
      },

      images() {
        const images = [];

        this.selectedItem.images.forEach((image) => {
          if (image.label && image.label.indexOf('itemDetails') === 0) {
            images.push({
              altText: this.selectedItem.displayName,
              source: image.url
            });
          }
        });

        return images;
      },

      maxAllowed() {
        const isUnlimited = this.selectedItem.maxAllowed === -1;

        let itemLimit = this.selectedItem.maxAllowed;

        if (!isUnlimited) itemLimit = Math.max((this.selectedItem.maxAllowed - this.existingQuantity), 0);

        const { orderMaximumItemCount } = this.orderValidations.validations;

        if (orderMaximumItemCount) {
          let cartLimit = Math.max(orderMaximumItemCount - (this.order.orderItemCount + this.order.orderItemStagedCount), 0);

          if (this.selectedOrderItem) cartLimit += this.selectedOrderItem.quantity;

          return isUnlimited ? cartLimit : Math.min(itemLimit, cartLimit);
        }

        return itemLimit;
      },

      priceWithModifiers() {
        return calculatePriceWithModifiers(this.selectedItem, this.modifiers) * this.quantity;
      },

      selectedModifiers() {
        if (this.selectedOrderItem) {
          return this.selectedOrderItem.modifiers;
        }

        if (this.selectedFavoriteItem) {
          return this.selectedFavoriteItem.modifiers;
        }

        return null;
      },

      showFavoriteButton() {
        return this.selectedLocation.storeId
          && this.selectedLocation.isReadyForOrdering
          && !this.menu.unavailablePerSchedule
          && this.selectedLocation.isCurrentServiceAvailable;
      },

      specialInstructionsValidations() {
        const maxLength = this.selectedLocation.pos.maxCharacterLimitForItemLevelInstructions;

        if (maxLength && maxLength > 0) {
          return { max: maxLength };
        }

        return {};
      }
    },

    watch: {
      'selectedItem.menuItemId': 'reset'
    },

    created() {
      this.createdHandler();
    },

    methods: {
      createdHandler() {
        if (this.selectedOrderItem) {
          this.specialInstructions = this.selectedOrderItem.specialInstructions;
          this.quantity = this.selectedOrderItem.quantity || 1;
        }

        else if (this.selectedFavoriteItem) {
          this.specialInstructions = this.selectedFavoriteItem.specialInstructions;
        }
      },

      dirtyFormHandler() {
        this.$refs.favoriteButton.resetIsFavorite();
      },

      modifiersSelectedHandler(modifiers) {
        this.modifiers = modifiers;
      },

      reset() {
        this.modifiers = [];
        this.specialInstructions = null;
        this.quantity = 1;
      },

      async updateOrder() {
        if (this.selectedOrderItem) {
          await this.$store.dispatch('orders/editItem', {
            item: {
              favoriteItemId: this.selectedItem.favoriteItemId,
              menuItemId: this.selectedItem.menuItemId,
              orderItemId: this.selectedOrderItem.orderItemId,
              specialInstructions: this.specialInstructions,
              quantity: this.quantity,
              modifiers: this.modifiers,
              categoryId: this.selectedCategory?.categoryId
            }
          });

          this.$emit('updated');
        }

        else {
          /**
            * there may not be a selected category if the item was added from menu search
            * where search results list items without context of the categories they belong to
          */

          const item = {
            isUpsell: this.selectedItem.isUpsell,
            favoriteItemId: this.selectedItem.favoriteItemId,
            maxAllowed: this.selectedItem.maxAllowed,
            menuItemId: this.selectedItem.menuItemId,
            specialInstructions: this.specialInstructions,
            quantity: this.quantity,
            modifiers: this.modifiers,
            categoryId: this.selectedCategory?.categoryId,
            displayName: this.selectedItem.displayName,
            price: this.selectedItem.price
          };

          await this.$store.dispatch('orders/addItem', { item });

          // this.$triggerGoogleTrackingEvent('AddItemToCart', 'Order');

          this.$triggerNewRelicEvent('AddItemToCart', 'Information', 'Added Item To Cart');

          this.$emit('added');
        }
      }
    }
  };
</script>


<style lang="sass">

.new-item-banner
  background: var(--color-danger)
  color: var(--color-light)
  width: fit-content
  height: 30px
  display: flex
  align-items: center
  font-size: var(--font-size-body-2)
  font-weight: 700
  border-radius: 5px
  padding: 0px 10px

.low-inventory-banner
  background: var(--color-warning)
  color: var(--color-dark)
  height: 30px
  display: flex
  font-size: var(--font-size-body-2)
  font-weight: 700
  border-radius: 5px
  padding: 5px

.low-inventory-content
  width: fit-content
  padding: 3px 6px

.low-inventory-amount
  background: #FFE083
  border-radius: 3px
  width: fit-content
  padding: 3px 6px

</style>
